import { ChangeDetectorRef, Component, Input, OnChanges, SimpleChanges } from "@angular/core";
import { BaseChartComponent } from "./base-chart.component";
import { ChartConfiguration, Plugin } from "chart.js";
import { DataPointDto } from "src/app/services/api";
import { WindSpeedAlertLevels } from "src/app/interfaces";
import { TranslateService } from "@ngx-translate/core";
import {
  CHART_LEVEL1_ZONE_COLOR,
  CHART_LEVEL2_ZONE_COLOR,
  CHART_LEVEL3_ZONE_COLOR,
  CHART_LEVEL4_ZONE_COLOR,
  PRIMARY_LINE_COLOR,
  SECONDARY_LINE_COLOR,
} from "src/app/constants/index";

@Component({
  selector: "app-wind-chart",
  templateUrl: "./chart.component.html",
  styleUrls: ["./chart.component.scss"],
})
export class WindChartComponent extends BaseChartComponent implements OnChanges {
  @Input() public windGustData?: DataPointDto[];
  @Input() public windSpeedAlertLevels?: WindSpeedAlertLevels;

  private windDataLabel: string;
  private windGustDataLabel: string;

  constructor(translate: TranslateService, cdr: ChangeDetectorRef) {
    super(translate, cdr);
    this.windDataLabel = translate.instant("STATION_PAGE.WIND");
    this.windGustDataLabel = translate.instant("STATION_PAGE.WIND_GUST");
  }

  public ngOnChanges(changes: SimpleChanges): void {
    // Update the data property for app-empty-data component
    if (this.data?.length || this.windGustData?.length) {
      // Force chart re-rendering when data changes
      if ((changes.data || changes.windGustData) && this.chartContainer) {
        setTimeout(() => {
          this.renderChart();
          this.cdr.detectChanges();
        }, 0);
        return;
      }
    }

    // Call parent's ngOnChanges to ensure base component logic runs
    super.ngOnChanges(changes);
  }

  private drawWindSpeedAlertLevelsPlugin: Plugin = {
    id: "windSpeed",
    beforeDraw: (): void => {
      if (this.chart) {
        const {
          ctx,
          chartArea: { left, top, right, bottom },
          scales: { y },
        } = this.chart;

        let level1 = this.windSpeedAlertLevels?.level1WindSpeed ? y.getPixelForValue(this.windSpeedAlertLevels.level1WindSpeed) : null;
        let level2 = this.windSpeedAlertLevels?.level2WindSpeed ? y.getPixelForValue(this.windSpeedAlertLevels.level2WindSpeed) : null;
        let level3 = this.windSpeedAlertLevels?.level3WindSpeed ? y.getPixelForValue(this.windSpeedAlertLevels.level3WindSpeed) : null;

        level3 = level3 || top;
        level2 = level2 || level3;
        level1 = level1 || level2;

        ctx.save();

        ctx.fillStyle = CHART_LEVEL1_ZONE_COLOR;
        ctx.fillRect(left, bottom, right, level1 - bottom);

        ctx.fillStyle = CHART_LEVEL2_ZONE_COLOR;
        ctx.fillRect(left, level1, right, level2 - level1);

        ctx.fillStyle = CHART_LEVEL3_ZONE_COLOR;
        ctx.fillRect(left, level2, right, level3 - level2);

        ctx.fillStyle = CHART_LEVEL4_ZONE_COLOR;
        ctx.fillRect(left, level3, right, top - level3);

        ctx.restore();
      }
    },
  };

  protected getChartConfig(): ChartConfiguration {
    const transformedData = this.data.map(point => {
      const timestamp = new Date(point.timestamp).getTime();
      if (!(point.value || point.value === 0)) {
        return { x: timestamp, y: null };
      } else if (
        this.windSpeedAlertLevels?.level1WindSpeed ||
        this.windSpeedAlertLevels?.level2WindSpeed ||
        this.windSpeedAlertLevels?.level3WindSpeed
      ) {
        return { x: timestamp, y: Math.max(point.value, 0) };
      } else {
        return { x: timestamp, y: point.value / 100 };
      }
    });

    const xSuggestedMax = !this.currentTime ? new Date().getTime() : null;
    const ySuggestedMax =
      this.windSpeedAlertLevels?.level3WindSpeed + 1 ||
      this.windSpeedAlertLevels?.level2WindSpeed ||
      this.windSpeedAlertLevels?.level1WindSpeed ||
      2;

    // Obtener las opciones comunes para este gráfico con la unidad " km/h"
    const commonOptions = this.getCommonChartOptions(xSuggestedMax, ySuggestedMax, " km/h");

    // Si existe windGustData se manejan dos datasets
    if (this.windGustData) {
      const transformedWindGustData = this.windGustData.map(point => {
        const timestamp = new Date(point.timestamp).getTime();
        if (!(point.value || point.value === 0)) {
          return { x: timestamp, y: null };
        } else if (
          this.windSpeedAlertLevels?.level1WindSpeed ||
          this.windSpeedAlertLevels?.level2WindSpeed ||
          this.windSpeedAlertLevels?.level3WindSpeed
        ) {
          return { x: timestamp, y: Math.max(point.value, 0) };
        } else {
          return { x: timestamp, y: point.value / 100 };
        }
      });

      return {
        type: "line",
        data: {
          datasets: [
            {
              data: transformedData,
              label: this.windDataLabel,
              borderColor: PRIMARY_LINE_COLOR,
              pointBackgroundColor: PRIMARY_LINE_COLOR,
              backgroundColor: PRIMARY_LINE_COLOR,
              borderWidth: 1.5,
              pointRadius: 1,
              tension: 0.5,
              minBarLength: 1,
              parsing: false,
            },
            {
              data: transformedWindGustData,
              label: this.windGustDataLabel,
              borderColor: SECONDARY_LINE_COLOR,
              pointBackgroundColor: SECONDARY_LINE_COLOR,
              backgroundColor: SECONDARY_LINE_COLOR,
              borderWidth: 1.5,
              pointRadius: 1,
              tension: 0.5,
              minBarLength: 2,
              parsing: false,
            },
          ],
        },
        options: {
          ...commonOptions,
          plugins: {
            legend: {
              display: true,
              labels: {
                boxHeight: 2,
                boxWidth: 24,
              },
            },
          },
        },
        plugins: [this.legendSpacingPlugin, this.drawWindSpeedAlertLevelsPlugin],
      };
    } else {
      // Caso sin windGustData
      return {
        type: "line",
        data: {
          datasets: [
            {
              data: transformedData,
              label: this.windDataLabel,
              borderColor: PRIMARY_LINE_COLOR,
              pointBackgroundColor: PRIMARY_LINE_COLOR,
              backgroundColor: PRIMARY_LINE_COLOR,
              borderWidth: 2,
              pointRadius: 1,
              tension: 0.5,
              minBarLength: 2,
              parsing: false,
            },
          ],
        },
        options: {
          ...commonOptions,
        },
        plugins: [this.drawWindSpeedAlertLevelsPlugin],
      };
    }
  }
}
